import React, { useState, useEffect } from 'react';
import {
    FaTimes,
    FaUser,
    FaEnvelope,
    FaMapMarkerAlt,
    FaSave,
    FaTags,
    FaToggleOn,
    FaToggleOff,
    FaTrash,
    FaPlus,
    FaSearch,
    FaCheckCircle,
    FaBuilding
} from 'react-icons/fa';
import styles from './EditContactModal.module.css';
import { contactService, ContactHelper } from '../../services/contactService';
import { contactTypeTagsService } from '../../services/contactTypeTagsService';
import { vwClientSummaryService } from '../../services/vwClientSummaryService';
import { useAccessToken, useLockBodyScroll } from '../../hooks';
import { useContactTypes } from '../../hooks/useContactTypes';
import { useModalAlert } from '../../hooks/useModalAlert';
import ModalAlert from './ModalAlert';

/**
 * U.S. States lookup for dropdown
 */
const US_STATES = [
    { code: 'AL', name: 'Alabama' },
    { code: 'AK', name: 'Alaska' },
    { code: 'AZ', name: 'Arizona' },
    { code: 'AR', name: 'Arkansas' },
    { code: 'CA', name: 'California' },
    { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' },
    { code: 'DE', name: 'Delaware' },
    { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' },
    { code: 'HI', name: 'Hawaii' },
    { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' },
    { code: 'IN', name: 'Indiana' },
    { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' },
    { code: 'KY', name: 'Kentucky' },
    { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' },
    { code: 'MD', name: 'Maryland' },
    { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' },
    { code: 'MN', name: 'Minnesota' },
    { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' },
    { code: 'MT', name: 'Montana' },
    { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' },
    { code: 'NH', name: 'New Hampshire' },
    { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' },
    { code: 'NY', name: 'New York' },
    { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' },
    { code: 'OH', name: 'Ohio' },
    { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' },
    { code: 'PA', name: 'Pennsylvania' },
    { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' },
    { code: 'SD', name: 'South Dakota' },
    { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' },
    { code: 'UT', name: 'Utah' },
    { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' },
    { code: 'WA', name: 'Washington' },
    { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' },
    { code: 'WY', name: 'Wyoming' }
];

/**
 * Edit/Add Contact Modal Component
 * Modal for editing an existing contact or creating a new one
 * Supports active/inactive status, deletion, and client-contact type management
 */
const EditContactModal = ({ contact, isOpen, onClose, onContactUpdated, mode = 'edit' }) => {
    const { getBackendAccessToken } = useAccessToken();
    const { modalAlert, setInfoAlert, setErrorAlert, clearAlert } = useModalAlert();
    const { contactTypes, loading: loadingContactTypes, loadContactTypes } = useContactTypes();

    // Lock body scroll when modal is open
    useLockBodyScroll(isOpen);

    // Determine if we're in create mode
    const isCreateMode = mode === 'create' || !contact;

    // Client search state (for create mode only)
    const [clientSearch, setClientSearch] = useState('');
    const [clientSearchResults, setClientSearchResults] = useState([]);
    const [searchingClients, setSearchingClients] = useState(false);
    const [selectedClients, setSelectedClients] = useState([]); // Array of {clientId, clientName}

    // Form state
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address1: '',
        address2: '',
        city: '',
        stateCode: '',
        zip: '',
        isSubscribed: true,
        clientPortalAccess: false,
        isActive: true
    });

    // Client-contact relationships and contact types
    const [clientContactTypes, setClientContactTypes] = useState([]); // All client-contact-type relationships for this contact
    const [loadingClientTypes, setLoadingClientTypes] = useState(false);
    const [clientTypeChanges, setClientTypeChanges] = useState({}); // Track changes per client
    
    // Store original form data to detect changes
    const [originalFormData, setOriginalFormData] = useState({});

    /**
     * Check if form data has changed from original
     */
    const checkForUnsavedChanges = (currentData, originalData) => {
        // Compare all form fields
        const fieldsToCheck = [
            'firstName', 'lastName', 'email', 'phone', 
            'address1', 'address2', 'city', 'stateCode', 'zip',
            'isSubscribed', 'clientPortalAccess', 'isActive'
        ];
        
        return fieldsToCheck.some(field => {
            const current = currentData[field];
            const original = originalData[field];
            
            // Handle boolean comparisons
            if (typeof current === 'boolean' && typeof original === 'boolean') {
                return current !== original;
            }
            
            // Handle string comparisons (treat empty strings and undefined/null as equivalent)
            const currentValue = current || '';
            const originalValue = original || '';
            return currentValue !== originalValue;
        });
    };

    /**
     * Check if contact type tags have changed
     */
    const checkForContactTypeChanges = (changes) => {
        // Check if any client has added or removed types
        return Object.values(changes).some(
            clientChanges => clientChanges.addedTypes.length > 0 || clientChanges.removedTypes.length > 0
        );
    };

    // UI state
    const [saving, setSaving] = useState(false);
    const [navigating, setNavigating] = useState(false); // Separate state for step navigation
    const [errors, setErrors] = useState({});
    // In create mode: 1: Contact Info, 2: Select Clients, 3: Client Contact Types, 4: Actions (if needed)
    // In edit mode: 1: Contact Info, 2: Client Contact Types, 3: Actions
    const [step, setStep] = useState(1);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
    const [flashSaveButton, setFlashSaveButton] = useState(false);

    /**
     * Initialize form data when contact changes or modal opens
     */
    useEffect(() => {
        if (isOpen) {
            if (isCreateMode) {
                // Initialize for create mode
                const initialData = {
                    firstName: '',
                    lastName: '',
                    email: '',
                    phone: '',
                    address1: '',
                    address2: '',
                    city: '',
                    stateCode: '',
                    zip: '',
                    isSubscribed: true,
                    clientPortalAccess: false,
                    isActive: true
                };
                
                setFormData(initialData);
                setOriginalFormData(initialData);
                setClientContactTypes([]);
                setClientTypeChanges({});
                setSelectedClients([]);
                setClientSearch('');
                setClientSearchResults([]);
                setErrors({});
                setStep(1);
                setHasUnsavedChanges(false);
                setShowUnsavedWarning(false);
                setFlashSaveButton(false);
            } else if (contact) {
                // Initialize for edit mode
                // Convert contact data to form format
                const isActiveBoolean = (() => {
                    if (contact.isActive === undefined || contact.isActive === null) return false;
                    if (typeof contact.isActive === 'boolean') return contact.isActive;
                    if (typeof contact.isActive === 'number') return contact.isActive === 1;
                    if (typeof contact.isActive === 'string') return contact.isActive.toLowerCase() === 'true' || contact.isActive === '1';
                    return false;
                })();

                const initialData = {
                    firstName: contact.firstName || '',
                    lastName: contact.lastName || '',
                    email: contact.email || '',
                    phone: contact.phone || '',
                    address1: contact.address1 || '',
                    address2: contact.address2 || '',
                    city: contact.city || '',
                    stateCode: contact.stateCode || '',
                    zip: contact.zip || '',
                    isSubscribed: contact.isSubscribed !== undefined ? contact.isSubscribed : true,
                    clientPortalAccess: contact.clientPortalAccess !== undefined ? contact.clientPortalAccess : false,
                    isActive: isActiveBoolean
                };

                setFormData(initialData);
                setOriginalFormData(initialData);
                setClientContactTypes([]);
                setClientTypeChanges({});
                setErrors({});
                setStep(1);
                setHasUnsavedChanges(false);
                setShowUnsavedWarning(false);
                setFlashSaveButton(false);
            }
        }
    }, [contact, isOpen, isCreateMode]);

    /**
     * Reset form when modal opens/closes
     */
    useEffect(() => {
        if (!isOpen) {
            setErrors({});
            setStep(1);
            setHasUnsavedChanges(false);
            setShowUnsavedWarning(false);
            setFlashSaveButton(false);
            clearAlert();
        }
    }, [isOpen, clearAlert]);

    /**
     * Handle form field changes
     */
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newFormData = {
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        };
        
        setFormData(newFormData);

        // Check for unsaved changes (both form and contact type tags)
        const formChanges = checkForUnsavedChanges(newFormData, originalFormData);
        const contactTypeChangesExist = checkForContactTypeChanges(clientTypeChanges);
        setHasUnsavedChanges(formChanges || contactTypeChangesExist);

        // Clear field error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    /**
     * Load client contact types for this contact
     */
    const loadClientContactTypes = async () => {
        if (!contact) return;
        
        setLoadingClientTypes(true);
        try {
            const accessToken = await getBackendAccessToken();
            const tags = await contactTypeTagsService.getTagsByContact(accessToken, contact.contactId);
            setClientContactTypes(tags);
            
            // Initialize changes tracker with current state
            const initialChanges = {};
            tags.forEach(tag => {
                if (!initialChanges[tag.clientId]) {
                    initialChanges[tag.clientId] = {
                        clientName: tag.clientName || `Client ${tag.clientId}`,
                        addedTypes: [],
                        removedTypes: [],
                        currentTypes: [tag.contactTypeId]
                    };
                } else {
                    initialChanges[tag.clientId].currentTypes.push(tag.contactTypeId);
                }
            });
            setClientTypeChanges(initialChanges);
        } catch (error) {
            console.error('Failed to load client contact types:', error);
            setErrorAlert(`Failed to load client contact types: ${error.message || 'Unknown error'}`);
        } finally {
            setLoadingClientTypes(false);
        }
    };

    /**
     * Handle contact type toggle for a specific client
     */
    const handleClientContactTypeToggle = (clientId, clientName, contactTypeId) => {
        setClientTypeChanges(prev => {
            const clientChanges = prev[clientId] || {
                clientName,
                addedTypes: [],
                removedTypes: [],
                currentTypes: []
            };
            
            const isCurrentlyAssigned = clientChanges.currentTypes.includes(contactTypeId);
            const isInAdded = clientChanges.addedTypes.includes(contactTypeId);
            const isInRemoved = clientChanges.removedTypes.includes(contactTypeId);
            
            let newAddedTypes = [...clientChanges.addedTypes];
            let newRemovedTypes = [...clientChanges.removedTypes];
            let newCurrentTypes = [...clientChanges.currentTypes];
            
            if (isCurrentlyAssigned) {
                // Currently has this type - toggle it off
                newCurrentTypes = newCurrentTypes.filter(id => id !== contactTypeId);
                if (isInAdded) {
                    // Was just added, so just remove from added list
                    newAddedTypes = newAddedTypes.filter(id => id !== contactTypeId);
                } else {
                    // Was there originally, add to removed list
                    if (!isInRemoved) {
                        newRemovedTypes.push(contactTypeId);
                    }
                }
            } else {
                // Currently doesn't have this type - toggle it on
                newCurrentTypes.push(contactTypeId);
                if (isInRemoved) {
                    // Was just removed, so remove from removed list
                    newRemovedTypes = newRemovedTypes.filter(id => id !== contactTypeId);
                } else {
                    // Wasn't there originally, add to added list
                    if (!isInAdded) {
                        newAddedTypes.push(contactTypeId);
                    }
                }
            }
            
            const updatedChanges = {
                ...prev,
                [clientId]: {
                    ...clientChanges,
                    addedTypes: newAddedTypes,
                    removedTypes: newRemovedTypes,
                    currentTypes: newCurrentTypes
                }
            };
            
            // Check for unsaved changes after updating contact type tags
            const formChanges = checkForUnsavedChanges(formData, originalFormData);
            const contactTypeChangesExist = checkForContactTypeChanges(updatedChanges);
            setHasUnsavedChanges(formChanges || contactTypeChangesExist);
            
            return updatedChanges;
        });
    };

    /**
     * Save contact type changes for all clients
     */
    const saveClientContactTypeChanges = async () => {
        const accessToken = await getBackendAccessToken();
        const changesExist = Object.values(clientTypeChanges).some(
            c => c.addedTypes.length > 0 || c.removedTypes.length > 0
        );
        
        if (!changesExist) {
            return; // No changes to save
        }
        
        const promises = [];
        
        for (const [clientId, changes] of Object.entries(clientTypeChanges)) {
            // Add new contact types
            for (const contactTypeId of changes.addedTypes) {
                promises.push(
                    contactTypeTagsService.createTag(accessToken, parseInt(clientId), contact.contactId, contactTypeId)
                );
            }
            
            // Remove contact types
            for (const contactTypeId of changes.removedTypes) {
                promises.push(
                    contactTypeTagsService.deleteTag(accessToken, parseInt(clientId), contact.contactId, contactTypeId)
                );
            }
        }
        
        await Promise.all(promises);
    };

    /**
     * Handle client search
     */
    const handleClientSearch = async (query) => {
        setClientSearch(query);
        
        if (query.trim().length < 2) {
            setClientSearchResults([]);
            return;
        }
        
        setSearchingClients(true);
        try {
            const accessToken = await getBackendAccessToken();
            const results = await vwClientSummaryService.searchClientsByName(accessToken, query);
            setClientSearchResults(results || []);
        } catch (error) {
            console.error('Failed to search clients:', error);
            setErrorAlert(`Failed to search clients: ${error.message || 'Unknown error'}`);
        } finally {
            setSearchingClients(false);
        }
    };

    /**
     * Handle client selection toggle
     */
    const handleClientToggle = (client) => {
        const isSelected = selectedClients.some(c => c.clientId === client.clientId);
        
        if (isSelected) {
            setSelectedClients(prev => prev.filter(c => c.clientId !== client.clientId));
        } else {
            setSelectedClients(prev => [...prev, {
                clientId: client.clientId,
                clientName: client.clientName || `Client ${client.clientId}`,
                clientCode: client.clientCode || ''
            }]);
        }
    };

    /**
     * Validate form data
     */
    const validateForm = () => {
        // Convert form data to DTO format (empty strings to null) before validation
        const dto = ContactHelper.createContactDto(formData);
        const validation = ContactHelper.validateContact(dto);
        
        if (!validation.valid) {
            const fieldErrors = {};
            validation.errors.forEach(error => {
                // Map error messages to field names
                if (error.includes('First name')) fieldErrors.firstName = error;
                else if (error.includes('Last name')) fieldErrors.lastName = error;
                else if (error.includes('Email')) fieldErrors.email = error;
                else if (error.includes('State code')) fieldErrors.stateCode = error;
                else if (error.includes('address1')) fieldErrors.address1 = error;
                else if (error.includes('address2')) fieldErrors.address2 = error;
                else if (error.includes('city')) fieldErrors.city = error;
                else if (error.includes('ZIP')) fieldErrors.zip = error;
            });
            setErrors(fieldErrors);
            return false;
        }

        setErrors({});
        return true;
    };

    /**
     * Handle next step navigation
     */
    const handleNext = async () => {
        if (step === 1) {
            // Check for unsaved changes on Step 1
            if (hasUnsavedChanges) {
                if (!showUnsavedWarning) {
                    // First attempt - show warning and flash save button
                    const warningMessage = isCreateMode 
                        ? 'You have unsaved changes. Please complete the form before proceeding, or click "Next" again to discard changes.'
                        : 'You have unsaved changes. Click "Save Changes" to save your work, or click "Next" again to proceed without saving.';
                    
                    setShowUnsavedWarning(true);
                    setErrorAlert(warningMessage);
                    
                    // Flash the save button (only in edit mode where save button exists on step 1)
                    if (!isCreateMode) {
                        setFlashSaveButton(true);
                        setTimeout(() => setFlashSaveButton(false), 2000);
                    }
                    
                    return;
                }
                // Second attempt - user confirmed, proceed without saving/discard changes
                // Reset the warning flag and allow navigation
                setShowUnsavedWarning(false);
                setFlashSaveButton(false);
                clearAlert();
            }
            
            // Moving from Contact Info to next step
            if (validateForm()) {
                if (isCreateMode) {
                    // In create mode, move to Select Clients (Step 2)
                    setShowUnsavedWarning(false);
                    setFlashSaveButton(false);
                    clearAlert();
                    setStep(2);
                } else {
                    // In edit mode, move to Client Contact Types (Step 2)
                    setNavigating(true);
                    try {
                        // Load contact types and client contact types
                        if (contactTypes.length === 0) {
                            await loadContactTypes();
                        }
                        await loadClientContactTypes();
                        
                        // Reset warning states
                        setShowUnsavedWarning(false);
                        setFlashSaveButton(false);
                        setHasUnsavedChanges(false); // Reset since we're discarding changes
                        clearAlert();
                        setStep(2);
                    } finally {
                        setNavigating(false);
                    }
                }
            }
        } else if (step === 2) {
            if (isCreateMode) {
                // In create mode: Moving from Select Clients to Client Contact Types (Step 3)
                // Create contact first, then set up client relationships
                if (!validateForm()) {
                    setStep(1);
                    return;
                }
                
                setNavigating(true);
                try {
                    const accessToken = await getBackendAccessToken();
                    
                    // Create the contact
                    const createdContact = await contactService.createContact(accessToken, formData);
                    
                    // Initialize client contact types for selected clients
                    if (contactTypes.length === 0) {
                        await loadContactTypes();
                    }
                    
                    // Set up clientTypeChanges for each selected client
                    const initialChanges = {};
                    selectedClients.forEach(client => {
                        initialChanges[client.clientId] = {
                            clientName: client.clientName,
                            addedTypes: [],
                            removedTypes: [],
                            currentTypes: []
                        };
                    });
                    setClientTypeChanges(initialChanges);
                    
                    // Store the created contact for later steps
                    // We'll use formData to store contactId temporarily
                    setFormData(prev => ({ ...prev, contactId: createdContact.contactId }));
                    
                    clearAlert();
                    setStep(3);
                } catch (error) {
                    setErrorAlert(`Failed to create contact: ${error.message || 'Unknown error'}`);
                } finally {
                    setNavigating(false);
                }
            } else {
                // In edit mode: Moving from Client Contact Types to Actions (Step 3)
                // Save any contact type changes before moving to actions
                setNavigating(true);
                try {
                    await saveClientContactTypeChanges();
                    clearAlert();
                    setStep(3);
                } catch (error) {
                    setErrorAlert(`Failed to save contact type changes: ${error.message || 'Unknown error'}`);
                } finally {
                    setNavigating(false);
                }
            }
        } else if (step === 3 && isCreateMode) {
            // In create mode: Moving from Client Contact Types to completion
            // Save contact type changes and close
            setNavigating(true);
            try {
                const accessToken = await getBackendAccessToken();
                
                // Save contact type assignments
                for (const [clientId, changes] of Object.entries(clientTypeChanges)) {
                    if (changes.currentTypes.length > 0) {
                        await contactTypeTagsService.assignContactTypesToContact(
                            accessToken,
                            parseInt(clientId),
                            formData.contactId,
                            changes.currentTypes
                        );
                    }
                }
                
                setInfoAlert('Contact created and associated with clients successfully!');
                
                if (onContactUpdated) {
                    onContactUpdated({ ...formData });
                }
                
                handleClose();
            } catch (error) {
                setErrorAlert(`Failed to save contact type changes: ${error.message || 'Unknown error'}`);
            } finally {
                setNavigating(false);
            }
        }
    };

    /**
     * Handle previous step
     */
    const handlePrevious = () => {
        setShowUnsavedWarning(false);
        setFlashSaveButton(false);
        clearAlert();
        if (step > 1) {
            setStep(step - 1);
        }
    };

    /**
     * Handle form submission for creating/updating contact
     */
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate one more time
        if (!validateForm()) {
            setStep(1); // Go back to step 1 if validation fails
            return;
        }

        setSaving(true);

        try {
            const accessToken = await getBackendAccessToken();

            if (isCreateMode) {
                // Create new contact
                const createdContact = await contactService.createContact(accessToken, formData);
                
                // Success message
                setInfoAlert(
                    `Contact "${ContactHelper.getFullName(createdContact)}" created successfully!`
                );

                // Call callback with created contact
                if (onContactUpdated) {
                    onContactUpdated(createdContact);
                }

                // Close modal
                handleClose();
            } else {
                // Update existing contact
                const updatedContact = await contactService.updateContact(accessToken, contact.contactId, formData);

                // Success!
                setInfoAlert(
                    `Contact "${ContactHelper.getFullName(updatedContact)}" updated successfully!`
                );

                // Update original form data to reflect saved state
                setOriginalFormData(formData);
                setHasUnsavedChanges(false);

                // Call callback with updated contact
                if (onContactUpdated) {
                    onContactUpdated(updatedContact);
                }

                // Close modal
                handleClose();
            }
        } catch (error) {
            setErrorAlert(`Failed to ${isCreateMode ? 'create' : 'update'} contact: ${error.message || 'Unknown error'}`);
        } finally {
            setSaving(false);
        }
    };

    /**
     * Handle toggle contact active status
     */
    const handleToggleStatus = async () => {
        if (!contact) return;

        const contactName = ContactHelper.getFullName(contact);
        const action = formData.isActive ? 'deactivate' : 'activate';
        const actionPast = formData.isActive ? 'deactivated' : 'activated';
        
        // Show confirmation dialog
        const confirmAction = window.confirm(
            `Are you sure you want to ${action} "${contactName}"?\n\n${
                formData.isActive 
                    ? 'This will hide the contact from most views in the system.'
                    : 'This will make the contact visible in the system again.'
            }`
        );
        
        if (!confirmAction) {
            return;
        }

        setSaving(true);

        try {
            const accessToken = await getBackendAccessToken();
            
            let updatedContact;
            if (formData.isActive) {
                // Currently active, so deactivate
                updatedContact = await contactService.deactivateContact(accessToken, contact.contactId);
            } else {
                // Currently inactive, so activate
                updatedContact = await contactService.activateContact(accessToken, contact.contactId);
            }
            
            setInfoAlert(`Contact "${contactName}" has been ${actionPast} successfully`);
            
            // Call callback with updated contact
            if (onContactUpdated) {
                onContactUpdated(updatedContact);
            }
            
            // Close modal after successful action
            handleClose();
        } catch (error) {
            console.error(`Failed to ${action} contact:`, error);
            setErrorAlert(`Failed to ${action} contact: ${error.message || 'Unknown error'}`);
        } finally {
            setSaving(false);
        }
    };

    /**
     * Handle delete contact
     */
    const handleDelete = async () => {
        if (!contact) return;

        const contactName = ContactHelper.getFullName(contact);
        
        // Show confirmation dialog
        const confirmDelete = window.confirm(
            `Are you sure you want to delete "${contactName}"?\n\nThis action cannot be undone and will permanently remove the contact from the system.`
        );
        
        if (!confirmDelete) {
            return;
        }
        
        setSaving(true);

        try {
            const accessToken = await getBackendAccessToken();
            
            await contactService.deleteContact(accessToken, contact.contactId);
            
            setInfoAlert(`Contact "${contactName}" has been successfully deleted`);
            
            // Call callback to refresh the list
            if (onContactUpdated) {
                onContactUpdated(null, true); // Pass true to indicate deletion
            }

            // Close modal
            handleClose();
        } catch (error) {
            console.error('Failed to delete contact:', error);
            
            // Extract the specific error message from the backend response
            let errorMessage = `Failed to delete contact "${contactName}".`;
            
            // Check if we have a structured error response with a reason
            if (error.response?.data?.reason) {
                errorMessage = error.response.data.reason;
            } else if (error.message && error.message !== 'Failed to delete contact') {
                // Use the parsed error message if it's meaningful
                errorMessage = error.message;
            } else {
                errorMessage += ' Please try again.';
            }
            
            setErrorAlert(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    /**
     * Handle modal close
     */
    const handleClose = () => {
        // Check for unsaved changes only on Step 1 (where editing happens)
        if (step === 1 && hasUnsavedChanges) {
            if (!showUnsavedWarning) {
                // First close attempt - show warning and flash save button
                const warningMessage = isCreateMode 
                    ? 'You have unsaved changes. Please save your work before closing, or click "Close" again to discard changes.'
                    : 'You have unsaved changes. Click "Save Changes" to save your work, or click "Close" again to discard changes.';
                
                setShowUnsavedWarning(true);
                setErrorAlert(warningMessage);
                
                // Flash the save button (only in edit mode where save button exists on step 1)
                if (!isCreateMode) {
                    setFlashSaveButton(true);
                    setTimeout(() => setFlashSaveButton(false), 2000);
                }
                
                return;
            }
        }
        
        // Second attempt or no unsaved changes - reset all states and close
        setErrors({});
        setStep(1);
        setHasUnsavedChanges(false);
        setShowUnsavedWarning(false);
        setFlashSaveButton(false);
        clearAlert();
        onClose();
    };

    // Don't render if not open
    if (!isOpen) return null;

    // Don't render if in edit mode and no contact
    if (!isCreateMode && !contact) return null;

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                {/* Modal Header */}
                <div className={styles.modalHeader}>
                    <div className={styles.headerInfo}>
                        <div className={styles.headerIcon}>
                            {isCreateMode ? <FaPlus /> : <FaUser />}
                        </div>
                        <div>
                            <h2 className={styles.modalTitle}>
                                {isCreateMode ? 'Add New Contact' : 'Edit Contact'}
                            </h2>
                            <p className={styles.modalSubtitle}>
                                {isCreateMode 
                                    ? 'Create a new contact and associate with clients'
                                    : ContactHelper.getFullName(contact)
                                }
                            </p>
                        </div>
                    </div>
                    <button
                        className={styles.closeButton}
                        onClick={handleClose}
                        title="Close"
                        disabled={saving}
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Modal Alert - Shows inside the modal */}
                <ModalAlert
                    alert={modalAlert}
                    onClose={clearAlert}
                    autoHideDuration={5000}
                />

                {/* Step 1 - Contact Information */}
                {step === 1 && (
                    <form onSubmit={handleSubmit} className={styles.form}>
                        {/* Step Indicator */}
                        <div className={styles.stepIndicator}>
                            <div className={`${styles.step} ${styles.active}`}>
                                <div className={styles.stepNumber}>1</div>
                                <div className={styles.stepLabel}>Edit Info</div>
                            </div>
                            <div className={styles.stepLine}></div>
                            <div className={styles.step}>
                                <div className={styles.stepNumber}>2</div>
                                <div className={styles.stepLabel}>Client Types</div>
                            </div>
                            <div className={styles.stepLine}></div>
                            <div className={styles.step}>
                                <div className={styles.stepNumber}>3</div>
                                <div className={styles.stepLabel}>Actions</div>
                            </div>
                        </div>

                        <div className={styles.formStep}>
                            <h3 className={styles.sectionTitle}>
                                <FaUser /> Contact Information
                            </h3>

                            {/* First Name and Last Name */}
                            <div className={styles.formRow}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="firstName" className={styles.label}>
                                        First Name <span className={styles.required}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="firstName"
                                        name="firstName"
                                        className={`${styles.input} ${errors.firstName ? styles.inputError : ''}`}
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        maxLength={50}
                                        disabled={saving}
                                        required
                                    />
                                    {errors.firstName && (
                                        <span className={styles.errorMessage}>{errors.firstName}</span>
                                    )}
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="lastName" className={styles.label}>
                                        Last Name
                                    </label>
                                    <input
                                        type="text"
                                        id="lastName"
                                        name="lastName"
                                        className={`${styles.input} ${errors.lastName ? styles.inputError : ''}`}
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        maxLength={50}
                                        disabled={saving}
                                    />
                                    {errors.lastName && (
                                        <span className={styles.errorMessage}>{errors.lastName}</span>
                                    )}
                                </div>
                            </div>

                            {/* Email */}
                            <div className={styles.formGroup}>
                                <label htmlFor="email" className={styles.label}>
                                    <FaEnvelope /> Email
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                                    value={formData.email}
                                    onChange={handleChange}
                                    maxLength={254}
                                    disabled={saving}
                                    placeholder="contact@example.com"
                                />
                                {errors.email && (
                                    <span className={styles.errorMessage}>{errors.email}</span>
                                )}
                            </div>

                            {/* Phone */}
                            <div className={styles.formGroup}>
                                <label htmlFor="phone" className={styles.label}>
                                    Phone
                                </label>
                                <input
                                    type="tel"
                                    id="phone"
                                    name="phone"
                                    className={`${styles.input} ${errors.phone ? styles.inputError : ''}`}
                                    value={formData.phone}
                                    onChange={handleChange}
                                    maxLength={20}
                                    disabled={saving}
                                    placeholder="(555) 123-4567"
                                />
                                {errors.phone && (
                                    <span className={styles.errorMessage}>{errors.phone}</span>
                                )}
                            </div>

                            {/* Address Section */}
                            <h4 className={styles.subsectionTitle}>
                                <FaMapMarkerAlt /> Address
                            </h4>

                            {/* Address Line 1 */}
                            <div className={styles.formGroup}>
                                <label htmlFor="address1" className={styles.label}>
                                    Address Line 1
                                </label>
                                <input
                                    type="text"
                                    id="address1"
                                    name="address1"
                                    className={`${styles.input} ${errors.address1 ? styles.inputError : ''}`}
                                    value={formData.address1}
                                    onChange={handleChange}
                                    maxLength={100}
                                    disabled={saving}
                                    placeholder="123 Main Street"
                                />
                                {errors.address1 && (
                                    <span className={styles.errorMessage}>{errors.address1}</span>
                                )}
                            </div>

                            {/* Address Line 2 */}
                            <div className={styles.formGroup}>
                                <label htmlFor="address2" className={styles.label}>
                                    Address Line 2
                                </label>
                                <input
                                    type="text"
                                    id="address2"
                                    name="address2"
                                    className={`${styles.input} ${errors.address2 ? styles.inputError : ''}`}
                                    value={formData.address2}
                                    onChange={handleChange}
                                    maxLength={100}
                                    disabled={saving}
                                    placeholder="Apt 4B, Suite 200"
                                />
                                {errors.address2 && (
                                    <span className={styles.errorMessage}>{errors.address2}</span>
                                )}
                            </div>

                            {/* City, State, ZIP */}
                            <div className={styles.formRow}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="city" className={styles.label}>
                                        City
                                    </label>
                                    <input
                                        type="text"
                                        id="city"
                                        name="city"
                                        className={`${styles.input} ${errors.city ? styles.inputError : ''}`}
                                        value={formData.city}
                                        onChange={handleChange}
                                        maxLength={100}
                                        disabled={saving}
                                        placeholder="New York"
                                    />
                                    {errors.city && (
                                        <span className={styles.errorMessage}>{errors.city}</span>
                                    )}
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="stateCode" className={styles.label}>
                                        State
                                    </label>
                                    <select
                                        id="stateCode"
                                        name="stateCode"
                                        className={`${styles.select} ${errors.stateCode ? styles.inputError : ''}`}
                                        value={formData.stateCode}
                                        onChange={handleChange}
                                        disabled={saving}
                                    >
                                        <option value="">Select State</option>
                                        {US_STATES.map(state => (
                                            <option key={state.code} value={state.code}>
                                                {state.code} - {state.name}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.stateCode && (
                                        <span className={styles.errorMessage}>{errors.stateCode}</span>
                                    )}
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="zip" className={styles.label}>
                                        ZIP Code
                                    </label>
                                    <input
                                        type="text"
                                        id="zip"
                                        name="zip"
                                        className={`${styles.input} ${errors.zip ? styles.inputError : ''}`}
                                        value={formData.zip}
                                        onChange={handleChange}
                                        maxLength={20}
                                        disabled={saving}
                                        placeholder="10001"
                                    />
                                    {errors.zip && (
                                        <span className={styles.errorMessage}>{errors.zip}</span>
                                    )}
                                </div>
                            </div>

                            {/* Status Options */}
                            <div className={styles.checkboxGroup}>
                                <label className={styles.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        name="isSubscribed"
                                        checked={formData.isSubscribed}
                                        onChange={handleChange}
                                        disabled={saving}
                                    />
                                    <span>Subscribed to communications</span>
                                </label>

                                <label className={styles.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        name="clientPortalAccess"
                                        checked={formData.clientPortalAccess}
                                        onChange={handleChange}
                                        disabled={saving}
                                    />
                                    <span>Client Portal Access</span>
                                </label>
                            </div>
                        </div>

                        {/* Form Actions */}
                        <div className={styles.formActions}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={handleClose}
                                disabled={saving}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className={`${styles.submitButton} ${flashSaveButton ? styles.flashButton : ''}`}
                                disabled={saving}
                            >
                                {saving ? (
                                    <>
                                        <span className={styles.spinner}></span>
                                        Saving Changes...
                                    </>
                                ) : (
                                    <>
                                        <FaSave /> Save Changes
                                    </>
                                )}
                            </button>
                            <button
                                type="button"
                                className={styles.nextButton}
                                onClick={handleNext}
                                disabled={saving}
                            >
                                Next: Client Types →
                            </button>
                        </div>
                    </form>
                )}

                {/* Step 2 - Client Contact Types */}
                {step === 2 && (
                    <div className={styles.form}>
                        {/* Step Indicator */}
                        <div className={styles.stepIndicator}>
                            <div className={styles.step}>
                                <div className={styles.stepNumber}>1</div>
                                <div className={styles.stepLabel}>Edit Info</div>
                            </div>
                            <div className={styles.stepLine}></div>
                            <div className={`${styles.step} ${styles.active}`}>
                                <div className={styles.stepNumber}>2</div>
                                <div className={styles.stepLabel}>Client Types</div>
                            </div>
                            <div className={styles.stepLine}></div>
                            <div className={styles.step}>
                                <div className={styles.stepNumber}>3</div>
                                <div className={styles.stepLabel}>Actions</div>
                            </div>
                        </div>

                        <div className={styles.formStep}>
                            <h3 className={styles.sectionTitle}>
                                <FaTags /> Manage Contact Types for Clients
                            </h3>

                            <p className={styles.helpText}>
                                Edit the contact types for this contact across different clients. 
                                Add or remove contact types to customize how this contact is categorized for each client.
                            </p>

                            {/* Add to New Clients Section */}
                            <div className={styles.addClientsSection}>
                                <h4 className={styles.subsectionTitle}>
                                    <FaPlus /> Add Contact to New Clients
                                </h4>
                                
                                <div className={styles.searchContainer}>
                                    <div className={styles.searchInputWrapper}>
                                        <FaSearch className={styles.searchIcon} />
                                        <input
                                            type="text"
                                            className={styles.searchInput}
                                            placeholder="Search clients to add..."
                                            value={clientSearch}
                                            onChange={(e) => handleClientSearch(e.target.value)}
                                            disabled={saving}
                                        />
                                        {clientSearch && (
                                            <button
                                                className={styles.clearSearchButton}
                                                onClick={() => {
                                                    setClientSearch('');
                                                    setClientSearchResults([]);
                                                }}
                                                title="Clear search"
                                                type="button"
                                            >
                                                <FaTimes />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Search Results */}
                                {searchingClients && (
                                    <div style={{ textAlign: 'center', padding: '1rem' }}>
                                        <div className={styles.spinner} style={{ margin: '0 auto' }}></div>
                                    </div>
                                )}
                                
                                {!searchingClients && clientSearchResults.length > 0 && (
                                    <div className={styles.searchResults}>
                                        {clientSearchResults
                                            .filter(client => !clientTypeChanges[client.clientId])
                                            .map(client => (
                                                <div key={client.clientId} className={styles.searchResultItem}>
                                                    <div className={styles.clientInfo}>
                                                        <FaBuilding className={styles.clientIcon} />
                                                        <div>
                                                            <div className={styles.clientName}>
                                                                {client.clientName || `Client ${client.clientId}`}
                                                            </div>
                                                            {client.clientCode && (
                                                                <div className={styles.clientCode}>{client.clientCode}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className={styles.addClientButton}
                                                        onClick={() => {
                                                            const clientId = client.clientId;
                                                            const clientName = client.clientName || `Client ${clientId}`;
                                                            
                                                            setClientTypeChanges(prev => ({
                                                                ...prev,
                                                                [clientId]: {
                                                                    clientName,
                                                                    addedTypes: [],
                                                                    removedTypes: [],
                                                                    currentTypes: []
                                                                }
                                                            }));
                                                            
                                                            setClientSearch('');
                                                            setClientSearchResults([]);
                                                            setInfoAlert(`Added "${clientName}" - now select contact types for this client below`);
                                                        }}
                                                        disabled={saving}
                                                    >
                                                        <FaPlus /> Add Client
                                                    </button>
                                                </div>
                                            ))}
                                        
                                        {clientSearchResults.every(client => clientTypeChanges[client.clientId]) && (
                                            <div style={{ 
                                                textAlign: 'center', 
                                                padding: '1rem',
                                                color: 'var(--text-secondary)',
                                                fontSize: '0.9rem'
                                            }}>
                                                All matching clients are already associated with this contact
                                            </div>
                                        )}
                                    </div>
                                )}

                                {!searchingClients && clientSearch.trim().length >= 2 && clientSearchResults.length === 0 && (
                                    <div style={{ 
                                        textAlign: 'center', 
                                        padding: '1rem',
                                        color: 'var(--text-secondary)',
                                        fontSize: '0.9rem'
                                    }}>
                                        No clients found matching "{clientSearch}"
                                    </div>
                                )}
                            </div>

                            {loadingClientTypes ? (
                                <div style={{ textAlign: 'center', padding: '2rem' }}>
                                    <div className={styles.spinner} style={{ margin: '0 auto' }}></div>
                                    <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>
                                        Loading client contact types...
                                    </p>
                                </div>
                            ) : Object.keys(clientTypeChanges).length === 0 ? (
                                <div style={{ 
                                    textAlign: 'center', 
                                    padding: '2rem', 
                                    backgroundColor: 'var(--bg-secondary)', 
                                    borderRadius: '8px',
                                    color: 'var(--text-secondary)'
                                }}>
                                    <p>This contact is not associated with any clients yet.</p>
                                    <p style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
                                        Use the search above to add clients.
                                    </p>
                                </div>
                            ) : (
                                <div>
                                    <h4 className={styles.subsectionTitle} style={{ marginTop: '2rem' }}>
                                        <FaTags /> Current Client Associations
                                    </h4>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                                        {Object.entries(clientTypeChanges).map(([clientId, changes]) => (
                                            <div key={clientId} className={styles.actionCard}>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <h4 className={styles.actionTitle}>
                                                        {changes.clientName}
                                                    </h4>
                                                    
                                                    {loadingContactTypes ? (
                                                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                                                            Loading contact types...
                                                        </p>
                                                    ) : (
                                                        <div style={{ 
                                                            display: 'flex', 
                                                            flexWrap: 'wrap', 
                                                            gap: '0.75rem',
                                                            marginTop: '1rem'
                                                        }}>
                                                            {contactTypes.map(type => {
                                                                const isSelected = changes.currentTypes.includes(type.contactTypeId);
                                                                const isAdded = changes.addedTypes.includes(type.contactTypeId);
                                                                const isRemoved = changes.removedTypes.includes(type.contactTypeId);
                                                                
                                                                return (
                                                                    <button
                                                                        key={type.contactTypeId}
                                                                        type="button"
                                                                        onClick={() => handleClientContactTypeToggle(
                                                                            parseInt(clientId), 
                                                                            changes.clientName, 
                                                                            type.contactTypeId
                                                                        )}
                                                                        disabled={saving}
                                                                        style={{
                                                                            padding: '0.5rem 1rem',
                                                                            borderRadius: '20px',
                                                                            border: isSelected 
                                                                                ? '2px solid var(--primary-color)' 
                                                                                : '2px solid var(--border-color)',
                                                                            backgroundColor: isSelected 
                                                                                ? '#ffffff' 
                                                                                : 'transparent',
                                                                            color: isSelected 
                                                                                ? 'var(--primary-color)' 
                                                                                : 'var(--text-primary)',
                                                                            cursor: 'pointer',
                                                                            fontSize: '0.9rem',
                                                                            fontWeight: '500',
                                                                            transition: 'all 0.2s',
                                                                            position: 'relative',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: '0.5rem'
                                                                        }}
                                                                        onMouseEnter={(e) => {
                                                                            if (!saving) {
                                                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                                                                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                                                                            }
                                                                        }}
                                                                        onMouseLeave={(e) => {
                                                                            e.currentTarget.style.transform = 'translateY(0)';
                                                                            e.currentTarget.style.boxShadow = 'none';
                                                                        }}
                                                                    >
                                                                        {type.contactType}
                                                                        {isAdded && (
                                                                            <span style={{ 
                                                                                fontSize: '0.75rem',
                                                                                backgroundColor: '#ffffff',
                                                                                color: '#28a745',
                                                                                padding: '0.125rem 0.5rem',
                                                                                borderRadius: '10px',
                                                                                fontWeight: '600',
                                                                                border: '1.5px solid #28a745'
                                                                            }}>
                                                                                NEW
                                                                            </span>
                                                                        )}
                                                                        {isRemoved && (
                                                                            <span style={{ 
                                                                                fontSize: '0.75rem',
                                                                                backgroundColor: 'rgba(220, 53, 69, 0.2)',
                                                                                color: 'var(--danger-color)',
                                                                                padding: '0.125rem 0.5rem',
                                                                                borderRadius: '10px',
                                                                                fontWeight: '600'
                                                                            }}>
                                                                                REMOVED
                                                                            </span>
                                                                        )}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Form Actions */}
                        <div className={styles.formActions}>
                            <button
                                type="button"
                                className={styles.backButton}
                                onClick={handlePrevious}
                                disabled={saving}
                            >
                                ← Back to Contact Info
                            </button>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={handleClose}
                                disabled={saving}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={styles.nextButton}
                                onClick={handleNext}
                                disabled={saving || loadingClientTypes}
                            >
                                {saving ? (
                                    <>
                                        <span className={styles.spinner}></span>
                                        Saving...
                                    </>
                                ) : (
                                    'Next: Actions →'
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 3 - Actions */}
                {step === 3 && (
                    <div className={styles.form}>
                        {/* Step Indicator */}
                        <div className={styles.stepIndicator}>
                            <div className={styles.step}>
                                <div className={styles.stepNumber}>1</div>
                                <div className={styles.stepLabel}>Edit Info</div>
                            </div>
                            <div className={styles.stepLine}></div>
                            <div className={styles.step}>
                                <div className={styles.stepNumber}>2</div>
                                <div className={styles.stepLabel}>Client Types</div>
                            </div>
                            <div className={styles.stepLine}></div>
                            <div className={`${styles.step} ${styles.active}`}>
                                <div className={styles.stepNumber}>3</div>
                                <div className={styles.stepLabel}>Actions</div>
                            </div>
                        </div>

                        <div className={styles.formStep}>
                            <h3 className={styles.sectionTitle}>
                                <FaTags /> Actions
                            </h3>

                            <p className={styles.helpText}>
                                These actions will be executed immediately after confirmation. No additional saving is required.
                            </p>

                            {/* Actions Grid */}
                            <div className={styles.actionsGrid}>
                                {/* Toggle Active Status */}
                                <div className={styles.actionCard}>
                                    <div className={styles.actionIcon}>
                                        {formData.isActive ? <FaToggleOn /> : <FaToggleOff />}
                                    </div>
                                    <div className={styles.actionInfo}>
                                        <h4 className={styles.actionTitle}>
                                            {formData.isActive ? 'Active Contact' : 'Inactive Contact'}
                                        </h4>
                                        <p className={styles.actionDescription}>
                                            {formData.isActive 
                                                ? 'Deactivate this contact to hide it from most views. You will be asked to confirm this action.'
                                                : 'Activate this contact to make it visible in the system. You will be asked to confirm this action.'
                                            }
                                        </p>
                                        <button
                                            type="button"
                                            className={`${styles.actionButton} ${formData.isActive ? styles.warning : styles.success}`}
                                            onClick={handleToggleStatus}
                                            disabled={saving}
                                        >
                                            {saving ? 'Processing...' : (formData.isActive ? 'Deactivate Contact' : 'Activate Contact')}
                                        </button>
                                    </div>
                                </div>

                                {/* Delete Contact */}
                                <div className={styles.actionCard}>
                                    <div className={`${styles.actionIcon} ${styles.danger}`}>
                                        <FaTrash />
                                    </div>
                                    <div className={styles.actionInfo}>
                                        <h4 className={styles.actionTitle}>Delete Contact</h4>
                                        <p className={styles.actionDescription}>
                                            Permanently remove this contact from the system. You will be asked to confirm this action, which cannot be undone.
                                        </p>
                                        <button
                                            type="button"
                                            className={`${styles.actionButton} ${styles.danger}`}
                                            onClick={handleDelete}
                                            disabled={saving}
                                        >
                                            {saving ? 'Deleting...' : 'Delete Contact'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Form Actions */}
                        <div className={styles.formActions}>
                            <button
                                type="button"
                                className={styles.backButton}
                                onClick={handlePrevious}
                                disabled={saving}
                            >
                                ← Back to Client Types
                            </button>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={handleClose}
                                disabled={saving}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EditContactModal;